<script setup>
import { ref, onMounted, computed } from 'vue'
import { fetchWrapper } from '@/helpers/fetch-wrapper'
import { useI18n } from 'vue-i18n'
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'

const { t, locale } = useI18n()

const props = defineProps({
  debug: { type: Boolean, default: false },
  mini: { type: Boolean, default: false }
})

const currentStreak = ref(0)
const month = ref({})
const today = ref({})
const overall = ref({})
const streakDays = ref([])

const fetchAchievementsData = async () => {
  if (props.debug) {
    // Return static data for testing
    return {
      current_streak: 5,
      today: {
        xp: 120,
        days_studied: 30,
        words_mastered: 50,
        flashcards_reviewed: 100,
        messages_sent: 10,
      },
      month: {
        xp: 300,
        days_studied: 20,
        words_mastered: 200,
        flashcards_reviewed: 500,
        messages_sent: 10,
      },
      overall: {
        xp: 2500,
        words_mastered: 1500,
        flashcards_reviewed: 3000,
        messages_sent: 150,
        topics_completed: 25,
      },
      streak_days: ['2025-06-09', '2025-06-10', '2025-06-11'],
    }
  } else {
    // Fetch data from the API
    return await fetchWrapper.get('/achievements/')
  }
}

onMounted(async () => {
  try {
    const response = await fetchAchievementsData()
    currentStreak.value = response.current_streak
    today.value = response.today
    month.value = response.month
    overall.value = response.overall || {}
    streakDays.value = response.streak_days
  } catch (error) {
    console.error('Failed to fetch achievements data:', error)
  }
})

// --- Mini mode logic ---
function getStartOfWeek(dateString) {
  // Parse the date string properly to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number)
  const d = new Date(year, month - 1, day) // month is 0-indexed
  
  const dayOfWeek = d.getDay() // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate days to subtract to get to Monday
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  
  // Get Monday of the current week
  d.setDate(d.getDate() - daysToSubtract)
  
  return d
}
const todayDate = computed(() => {
  // Use the real today or fallback to the first streak day in debug
  if (props.debug && streakDays.value.length) {
    return streakDays.value[streakDays.value.length - 1]
  }
  const d = new Date()
  return d.toISOString().slice(0, 10)
})
const weekDates = computed(() => {
  // Get array of ISO dates for the current week (Mon-Sun)
  const start = getStartOfWeek(todayDate.value)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d.toISOString().slice(0, 10)
  })
})

// Computed stats for different time periods
const todayStats = computed(() => [
  {
    key: 'xp',
    icon: '⚡',
    value: today.value.xp || 0,
    label: t('achievements.xp')
  },
  {
    key: 'words_mastered',
    icon: '📚',
    value: today.value.words_mastered || 0,
    label: t('achievements.words-mastered')
  },
  {
    key: 'flashcards_reviewed',
    icon: '🎯',
    value: today.value.flashcards_reviewed || 0,
    label: t('achievements.flashcards-reviewed')
  },
  {
    key: 'messages_sent',
    icon: '💬',
    value: today.value.messages_sent || 0,
    label: t('achievements.messages-sent')
  },
  {
    key: 'topics_completed',
    icon: '🏆',
    value: today.value.topics_completed || 0,
    label: t('achievements.topics-completed')
  }
])

const monthStats = computed(() => [
  {
    key: 'xp',
    icon: '⚡',
    value: month.value.xp || 0,
    label: t('achievements.xp')
  },
  {
    key: 'words_mastered',
    icon: '📚',
    value: month.value.words_mastered || 0,
    label: t('achievements.words-mastered')
  },
  {
    key: 'flashcards_reviewed',
    icon: '🎯',
    value: month.value.flashcards_reviewed || 0,
    label: t('achievements.flashcards-reviewed')
  },
  {
    key: 'messages_sent',
    icon: '💬',
    value: month.value.messages_sent || 0,
    label: t('achievements.messages-sent')
  },
  {
    key: 'topics_completed',
    icon: '🏆',
    value: month.value.topics_completed || 0,
    label: t('achievements.topics-completed')
  }
])

const overallStats = computed(() => [
  {
    key: 'xp',
    icon: '⚡',
    value: overall.value.xp || 0,
    label: t('achievements.xp')
  },
  {
    key: 'words_mastered',
    icon: '📚',
    value: overall.value.words_mastered || 0,
    label: t('achievements.words-mastered')
  },
  {
    key: 'flashcards_reviewed',
    icon: '🎯',
    value: overall.value.flashcards_reviewed || 0,
    label: t('achievements.flashcards-reviewed')
  },
  {
    key: 'messages_sent',
    icon: '💬',
    value: overall.value.messages_sent || 0,
    label: t('achievements.messages-sent')
  },
  {
    key: 'topics_completed',
    icon: '🏆',
    value: overall.value.topics_completed || 0,
    label: t('achievements.topics-completed')
  }
])
</script>

<template>
  <div class="achievement-display" :class="{ mini: props.mini }">
    <template v-if="props.mini">
      <div class="mini-row">
        {{ t('register.you-have-earned:') }} &nbsp;<span class="mini-xp">{{ today.xp }}   XP!</span>&nbsp;
        <span class="mini-star ml-1">⭐</span>
      </div>
      <hr>
      <div class="mini-week">
        <span
          v-for="date in weekDates"
          :key="date"
          class="mini-day"
          :class="{
            streak: streakDays.includes(date),
          }"
        > 
          <div class="mini-weekday">{{ new Date(date + ' 00:00:00').toLocaleDateString(locale, { weekday: 'short' }) }}
          </div>
          <span v-if="date === todayDate " class="mini-check">✅</span>
          <span v-else-if="streakDays.includes(date)"> &nbsp;</span>
          <span style="font-size: .4em" v-else>&nbsp;</span>
        </span>
      </div>
    </template>
    <template v-else>
      <!-- Hero Section with Streak -->
      <div class="hero-section">
        <div class="streak-hero">
          <div class="streak-flame-large">🔥</div>
          <div class="streak-info">
            <div class="streak-number-hero">{{ currentStreak }}</div>
            <div class="streak-label-hero">{{ t('achievements.day-streak') }}</div>
          </div>
        </div>
        <div class="hero-message">
          <h1 class="hero-title">{{ t('achievements.your-progress') }}</h1>
          <p class="hero-subtitle">{{ t('achievements.keep-up-the-great-work') }}</p>
        </div>
      </div>

      <!-- Stats Cards Section -->
      <div class="stats-grid">
        <!-- Today's Stats -->
        <div class="stats-card today-card">
          <div class="card-header">
            <div class="card-icon">📅</div>
            <div class="card-title">{{ t('achievements.today') }}</div>
          </div>
          <div class="stats-content">
            <div class="stat-item" v-for="(stat, index) in todayStats" :key="stat.key" :style="{ '--delay': index * 0.1 + 's' }">
              <div class="stat-icon">{{ stat.icon }}</div>
              <div class="stat-info">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- This Month's Stats -->
        <div class="stats-card month-card">
          <div class="card-header">
            <div class="card-icon">📊</div>
            <div class="card-title">{{ t('achievements.this-month') }}</div>
          </div>
          <div class="stats-content">
            <div class="stat-item" v-for="(stat, index) in monthStats" :key="stat.key" :style="{ '--delay': index * 0.1 + 's' }">
              <div class="stat-icon">{{ stat.icon }}</div>
              <div class="stat-info">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Overall Stats -->
        <div class="stats-card overall-card">
          <div class="card-header">
            <div class="card-icon">🏆</div>
            <div class="card-title">{{ t('achievements.overall') }}</div>
          </div>
          <div class="stats-content">
            <div class="stat-item" v-for="(stat, index) in overallStats" :key="stat.key" :style="{ '--delay': index * 0.1 + 's' }">
              <div class="stat-icon">{{ stat.icon }}</div>
              <div class="stat-info">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Calendar Section -->
      <div class="calendar-section">
        <div class="calendar-header">
          <h3 class="calendar-title">{{ t('achievements.streak-calendar') }}</h3>
          <p class="calendar-subtitle">{{ t('achievements.your-learning-journey') }}</p>
        </div>
        <div class="calendar-wrapper">
          <VueCal
            :events="streakDays.map(date => ({ start: date, end: date, title: 'Streak Day' }))"
            :disable-views="['years', 'year', 'week', 'day']"
            :locale="$i18n.locale"
            class="streak-calendar"
          >
            <template #cell-content="{ cell }">
              <div class="custom-cell">
                <span>{{ cell.content }}</span>
                <span v-if="streakDays.includes(cell.formattedDate)" class="star-icon">⭐</span>
                <span v-else>&nbsp;</span>
              </div>
            </template>
          </VueCal>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.achievement-display {
  padding: 20px;
  margin: 0 auto;
  box-sizing: border-box;
  display: block;
  background: white;
  border-radius: 20px;
  color: #333;
  position: relative;
  overflow: hidden;
  min-height: 600px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.achievement-display::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 20%, rgba(102, 126, 234, 0.03) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(118, 75, 162, 0.02) 0%, transparent 50%);
  pointer-events: none;
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 2rem 0;
  position: relative;
  z-index: 1;
  animation: fadeInDown 0.8s ease-out;
}

.streak-hero {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.streak-flame-large {
  font-size: 4rem;
  animation: flicker 2s ease-in-out infinite alternate;
}

@keyframes flicker {
  0%, 100% { transform: scale(1) rotate(-3deg); }
  50% { transform: scale(1.1) rotate(3deg); }
}

.streak-info {
  text-align: left;
}

.streak-number-hero {
  font-size: 3.5rem;
  font-weight: bold;
  line-height: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.streak-label-hero {
  font-size: 1.2rem;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 0.5rem;
  color: #666;
}

.hero-message {
  animation: fadeIn 1s ease-out 0.5s both;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  color: #333;
}

.hero-subtitle {
  font-size: 1.1rem;
  opacity: 0.7;
  margin: 0;
  color: #666;
}

@keyframes fadeInDown {
  from { transform: translateY(-30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
  position: relative;
  z-index: 1;
}

.stats-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  animation: slideInUp 0.8s ease-out;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.stats-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.stats-card:nth-child(1) { animation-delay: 0.2s; }
.stats-card:nth-child(2) { animation-delay: 0.4s; }
.stats-card:nth-child(3) { animation-delay: 0.6s; }

@keyframes slideInUp {
  from { transform: translateY(50px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.card-icon {
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 10px;
}

.card-title {
  font-size: 1.3rem;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  color: #333;
}

.stats-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 12px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.6s ease-out;
  animation-fill-mode: both;
  animation-delay: calc(1s + var(--delay, 0s));
}

.stat-item:hover {
  background: rgba(102, 126, 234, 0.1);
  transform: translateX(5px);
}

@keyframes fadeInUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.stat-icon {
  font-size: 1.25rem;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 8px;
  flex-shrink: 0;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 1.4rem;
  font-weight: bold;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  color: #333;
}

.stat-label {
  font-size: 0.85rem;
  opacity: 0.7;
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
}

/* Calendar Section */
.calendar-section {
  margin-top: 3rem;
  position: relative;
  z-index: 1;
  animation: fadeIn 1s ease-out 1.5s both;
}

.calendar-header {
  text-align: center;
  margin-bottom: 2rem;
}

.calendar-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  color: #333;
}

.calendar-subtitle {
  font-size: 1rem;
  opacity: 0.7;
  margin: 0;
  color: #666;
}

.calendar-wrapper {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.streak-calendar {
  border-radius: 12px;
  overflow: hidden;
}

.custom-cell {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.star-icon {
  color: #ff6b6b;
  font-size: 1.2em;
  margin-top: 5px;
  animation: twinkle 2s ease-in-out infinite;
}

@keyframes twinkle {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
}

/* Today/Month/Overall Card Specific Colors */
.today-card {
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.2), rgba(255, 165, 0, 0.2));
  border-color: rgba(255, 107, 107, 0.3);
}

.month-card {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2));
  border-color: rgba(102, 126, 234, 0.3);
}

.overall-card {
  background: linear-gradient(135deg, rgba(46, 213, 115, 0.2), rgba(0, 184, 148, 0.2));
  border-color: rgba(46, 213, 115, 0.3);
}

/* Only apply these styles when mini is true */
.achievement-display.mini {
  padding: 8px 8px;
  background: #f8f8ff;
  border-radius: 8px;
  display: block;
  min-width: 180px;
  max-width: 320px;
  margin: 0 auto;
  box-sizing: border-box;
  color: #333;
  min-height: unset;
}

.achievement-display.mini .mini-row {
  display: flex;
  align-items: center;
  font-size: 1.1em;
  margin-bottom: 2px;
  justify-content: center;
}

.achievement-display.mini .mini-star {
  font-size: 1.5em;
  margin-right: 8px;
}

.achievement-display.mini .mini-xp {
  font-weight: bold;
  color: #6c47ff;
}

.achievement-display.mini .mini-week {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  font-size: 1.3em;
  margin-top: 2px;
  margin-bottom: 2px;
  min-width: 140px;
  max-width: 100%;
  overflow-x: auto;
}

.achievement-display.mini .mini-day {
  width: 2.2em;
  text-align: center;
  line-height: 1.8em;
  border-radius: 50%;
  background: transparent;
  font-size: 1.2em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
}

.achievement-display.mini .mini-weekday {
  font-size: 0.75em;
  color: #888;
  margin-bottom: 2px;
  line-height: 1;
}

.achievement-display.mini .mini-day.today {
  background: #e0e7ff;
  font-weight: bold;
}

.achievement-display.mini .mini-day.checked {
  background: #a7f3d0;
  color: #059669;
  font-weight: bold;
  border: 2px solid #059669;
}

.achievement-display.mini .mini-check {
  font-size: 1.3em;
  color: #059669;
}

/* Responsive Design */
@media (max-width: 768px) {
  .achievement-display {
    padding: 15px;
    margin: 10px;
    border-radius: 16px;
  }

  .hero-title {
    font-size: 2rem;
  }

  .streak-number-hero {
    font-size: 2.5rem;
  }

  .streak-flame-large {
    font-size: 3rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin: 1.5rem 0;
  }

  .stats-card {
    padding: 1.25rem;
  }

  .calendar-section {
    margin-top: 2rem;
  }

  .calendar-wrapper {
    padding: 1rem;
  }

  .achievement-display.mini {
    padding: 8px 2vw;
    max-width: 98vw;
    min-width: 0;
    width: 100%;
  }

  .achievement-display.mini .mini-week {
    font-size: 1em;
    min-width: 0;
    max-width: 100vw;
  }

  .achievement-display.mini .mini-row {
    font-size: 1em;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 1.5rem 0;
  }

  .streak-hero {
    flex-direction: column;
    gap: 0.5rem;
  }

  .streak-info {
    text-align: center;
  }

  .hero-title {
    font-size: 1.75rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .stats-card {
    padding: 1rem;
  }

  .card-title {
    font-size: 1.1rem;
  }

  .stat-value {
    font-size: 1.2rem;
  }
}
</style>